import { ActionIcon, Card, Center, CopyButton, Divider, Group, Image, Loader, rem, Stack, Text } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import {
  IconBrandGithub,
  IconBrandGitlab,
  IconBrandLinkedin,
  IconBrandPaypal,
  IconCheck,
  IconCopy,
  IconExternalLink,
  IconEye,
  IconEyeOff,
  IconQrcode
} from "@tabler/icons-react"
import { t } from "i18next"
import { useTranslation } from "react-i18next"
import { ICrypto, IExternalLink } from "../Pages/Community/Props"
import { IMember, IPartner } from "../Pages/Team/Props"

import { modals } from "@mantine/modals"
import React from "react"
import classes from "./Renders.module.css"

export function renderPageHeader(title: string, subtitle: string): React.ReactElement {
  return (
    <Stack gap={rem(0)} align="flex-start" justify="center">
      <Text className={classes.title}>{title}</Text>
      <Text className={classes.subtitle} style={{ whiteSpace: "pre-line" }}>
        {subtitle}
      </Text>
    </Stack>
  )
}

export function renderFooter(): React.ReactElement {
  return (
    <footer>
      <Stack gap={rem(8)} justify="center" align="center" className={classes.footer}>
        <Text size={rem(14)}>© 2018-2025 Aurora OSS</Text>
        <Text size={rem(12)}>{t("footer.copyright")}</Text>
        <Text size={rem(12)} c={"dimmed"} style={{ whiteSpace: "pre-line", textAlign: "center" }}>
          {"All trademarks are property of their respective owners."}
        </Text>

        <ActionIcon.Group>
          <ActionIcon
            variant="default"
            size="lg"
            aria-label="gitlab-link"
            onClick={() => window.open("https://gitlab.com/AuroraOSS")}
          >
            <IconBrandGitlab size={20} stroke={1.5} />
          </ActionIcon>

          <ActionIcon
            variant="default"
            size="lg"
            aria-label="github-link"
            onClick={() => window.open("https://github.com/whyorean")}
          >
            <IconBrandGithub size={20} stroke={1.5} />
          </ActionIcon>

          <ActionIcon
            variant="default"
            size="lg"
            aria-label="linkedin-link"
            onClick={() => window.open("https://www.linkedin.com/in/whyorean/")}
          >
            <IconBrandLinkedin size={20} stroke={1.5} />
          </ActionIcon>

          <ActionIcon
            variant="default"
            size="lg"
            aria-label="Likes"
            onClick={() => window.open("https://www.paypal.com/paypalme/auroradev")}
          >
            <IconBrandPaypal size={20} stroke={1.5} />
          </ActionIcon>
        </ActionIcon.Group>
      </Stack>
    </footer>
  )
}

export function renderHeader(title: string, subtitle?: string): React.ReactElement {
  return (
    <Stack gap={rem(8)} align="flex-start" justify="center">
      <Text size={rem(24)} fw={500} opacity={0.95} style={{ letterSpacing: "0.025em" }}>
        {title}
      </Text>
      {subtitle && (
        <Text size={rem(15)} fw={300} style={{ whiteSpace: "pre-line" }}>
          {subtitle}
        </Text>
      )}
    </Stack>
  )
}

export function renderFormHeader(title: string, subtitle: string): React.ReactElement {
  return (
    <Stack gap={rem(2)} align="flex-start" justify="center">
      <Text size={"sm"} fw={500}>
        {title}
      </Text>
      <Text size={"xs"} c={"dimmed"} style={{ letterSpacing: "0.025em" }}>
        {subtitle}
      </Text>
    </Stack>
  )
}

export function renderMultiLineEntry(title: string, subtitle: string): React.ReactElement {
  return (
    <Stack gap={rem(2)} align="flex-start" justify="center">
      <Text fw={700} className={classes.line1}>
        {title}
      </Text>
      <Text fz={rem(14)} fw={300}>
        {subtitle}
      </Text>
    </Stack>
  )
}

export function MultiLineHiddenEntry({ title, subtitle }: { title: string; subtitle: string }): React.ReactElement {
  const [opened, { toggle }] = useDisclosure()

  return (
    <Stack gap={rem(2)} justify="center" w={"100%"}>
      <Text size={"sm"} opacity={0.75}>
        {title}
      </Text>
      <Group justify="space-between" align="center" wrap={"nowrap"}>
        <Text size="sm">{opened ? subtitle : "xxxxxx@xxxxx"}</Text>
        <ActionIcon
          style={{ border: 0 }}
          variant={"default"}
          onClick={() => toggle()}
          aria-label="toggle-email-visibility"
        >
          {opened ? <IconEyeOff size={19} /> : <IconEye size={19} />}
        </ActionIcon>
      </Group>
    </Stack>
  )
}

export function renderEntryWithCopy(label: string, value: string): React.ReactElement {
  return (
    <Group gap={rem(2)} align="center" justify={"space-between"} wrap={"nowrap"}>
      <Text size={"xs"}>{label}</Text>

      <CopyButton value={value} timeout={2000}>
        {({ copied, copy }) => (
          <ActionIcon variant="subtle" color={copied ? "green" : "dimmed"} onClick={copy} aria-label="copy-button">
            {copied ? <IconCheck size={19} /> : <IconCopy size={19} />}
          </ActionIcon>
        )}
      </CopyButton>
    </Group>
  )
}

export function renderEntryWithOpen(label: string, url: string): React.ReactElement {
  return (
    <Group gap={rem(2)} align="center" justify={"space-between"} wrap={"nowrap"}>
      <Text size={"xs"}>{label}</Text>

      <ActionIcon variant="subtle" color="dimmed" onClick={() => window.open(url)} aria-label="open-button">
        <IconExternalLink size={19} />
      </ActionIcon>
    </Group>
  )
}

export function renderCrypto(option: ICrypto): React.ReactElement {
  const openModal = () =>
    modals.open({
      title: "",
      children: (
        <Stack align="center" justify="center">
          <Text size="md">Scan this QR code to send {option.name}</Text>
          <Image
            src={option.qr}
            alt={`QR code for ${option.name}`}
            bg={"white"}
            width={rem(160)}
            height={rem(160)}
            p={"lg"}
          />
        </Stack>
      )
    })

  return (
    <Card withBorder>
      <Group justify={"space-between"}>
        <Group wrap={"nowrap"}>
          <Image
            src={option.imageUrl}
            fallbackSrc="https://placehold.co/36x36.png"
            h={24}
            w={"auto"}
            aria-label={`crypto-icon-${option.name}`}
            alt={`crypto-icon-${option.name}`}
          />
          <Text className={classes.line1}>{option.name}</Text>
        </Group>
        <Group gap={rem(4)} wrap={"nowrap"}>
          <ActionIcon variant="subtle" color="dimmed" aria-label="qr-code" onClick={openModal}>
            <IconQrcode size={19} />
          </ActionIcon>
          <CopyButton value={option.address} timeout={2000}>
            {({ copied, copy }) => (
              <ActionIcon variant="subtle" color={copied ? "green" : "dimmed"} onClick={copy} aria-label="copy-button">
                {copied ? <IconCheck size={19} /> : <IconCopy size={19} />}
              </ActionIcon>
            )}
          </CopyButton>
        </Group>
      </Group>
    </Card>
  )
}

export function renderExternalLink(externalLink: IExternalLink): React.ReactElement {
  const openModal = () =>
    modals.open({
      title: "",
      children: (
        <Stack align="center" justify="center">
          <Text size="md">Scan this QR code to send {externalLink.name}</Text>
          <Image
            src={externalLink.qr}
            alt={`QR code for ${externalLink.name}`}
            bg={"white"}
            width={rem(160)}
            height={rem(160)}
            p={"lg"}
          />
        </Stack>
      )
    })
  return (
    <Card withBorder onClick={() => window.open(externalLink.url)} style={{ cursor: "pointer" }}>
      <Group wrap={"nowrap"}>
        <Image
          src={externalLink.imageUrl}
          fallbackSrc="https://placehold.co/36x36.png"
          h={24}
          w={"auto"}
          aria-label={`payment-icon-${externalLink.name}`}
          alt={`payment-icon-${externalLink.name}`}
        />
        <Text className={classes.line1}>{externalLink.name}</Text>
        {externalLink.qr && (
          <ActionIcon
            variant="subtle"
            color="dimmed"
            aria-label="qr-code"
            onClick={(e) => {
              e.stopPropagation()
              openModal()
            }}
          >
            <IconQrcode size={19} />
          </ActionIcon>
        )}
      </Group>
    </Card>
  )
}

export function Member({ member }: { member: IMember }): React.ReactElement {
  const { t } = useTranslation()
  return (
    <Card withBorder p={"md"}>
      <Group wrap={"nowrap"} justify={"flex-start"}>
        <Image
          src={member.image || member.image}
          fallbackSrc="https://placehold.co/36x36.png"
          radius={"xl"}
          w={36}
          h={36}
          alt="user-avatar"
          aria-label="user-avatar"
          loading={"eager"}
        />
        <Stack gap={rem(2)}>
          <Text size={"md"} className={classes.line1}>
            {member.name}
          </Text>
          <Text size={"sm"} opacity={0.8} className={classes.line2}>
            {member.username}
          </Text>
        </Stack>
      </Group>
      {member.email && (
        <>
          <Divider my={rem(8)} />
          <MultiLineHiddenEntry title={t("team.email")} subtitle={member.email} />
        </>
      )}
    </Card>
  )
}

export function renderSupporter(option: IPartner): React.ReactElement {
  return (
    <Card withBorder p={"md"} onClick={() => window.open(option.website)} style={{ cursor: "pointer" }}>
      <Group>
        <Image
          src={option.image || option.image}
          fallbackSrc="https://placehold.co/36x36.png"
          radius={"xl"}
          w={36}
          h={36}
          miw={36}
          mih={36}
          alt="supporter-avatar"
          aria-label="supporter-avatar"
          loading={"eager"}
        />
        <Stack gap={rem(2)}>
          <Text size={"md"} className={classes.line1}>
            {option.name}
          </Text>
          <Text size={"sm"} className={classes.line2}>
            {option.role}
          </Text>
        </Stack>
      </Group>
    </Card>
  )
}

export function Splash() {
  return (
    <Center h="100vh">
      <Loader color={"primary"} type={"bars"} />
    </Center>
  )
}
